import { useEffect, useRef, useState, type FormEvent } from 'react'

export const MAX_DASHBOARD_NAME = 80

// MP-OS-189 — name a dashboard BEFORE it exists (and rename one the same way). Nothing is
// created or changed until Save/Enter; Cancel, Escape or a click outside leaves no trace.
// A plain <input> with no key handling of its own: Cmd/Ctrl+A, arrows and Home/End are the
// browser's, and nothing in the app intercepts them globally.
export function NameDialog({
  title,
  initialName,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string
  initialName: string
  confirmLabel: string
  onConfirm: (name: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initialName)
  const inputRef = useRef<HTMLInputElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const trimmed = name.trim()

  // Focus the field with its text selected, so typing replaces the suggestion; give focus back
  // to whatever opened the dialog when it closes.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    inputRef.current?.focus()
    inputRef.current?.select()
    return () => { if (previous?.isConnected) previous.focus() }
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (trimmed) onConfirm(trimmed)
  }

  return (
    <div className="name-dialog-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div
        ref={dialogRef}
        className="name-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="name-dialog-title"
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); return }
          if (e.key !== 'Tab') return
          const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('input, button:not([disabled])') ?? [])
          const first = controls[0], last = controls[controls.length - 1]
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
        }}
      >
        <form onSubmit={submit}>
          <h2 id="name-dialog-title" className="name-dialog-title">{title}</h2>
          <input
            ref={inputRef}
            className="name-dialog-input"
            aria-label="Dashboard name"
            value={name}
            maxLength={MAX_DASHBOARD_NAME}
            onChange={(e) => setName(e.target.value)}
          />
          {!trimmed && <p className="name-dialog-hint">Enter a name.</p>}
          <div className="name-dialog-actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="submit" className="primary" disabled={!trimmed}>{confirmLabel}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
